import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { startImportToBoardThunk } from '@common-reducers/BoardsThunks';
import { setIsImportModalOpen } from '@common-reducers/CampaignReducer';
import { importItemsActions } from '@common-reducers/ImportItemsReducer';
import { modalActions } from '@common-reducers/ModalReducer';
import { dynamicColumnList } from '@common-reducers/TaskThunks';
import { namespace } from '@common/config/constants';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ImportButton from './import-button';
import { countValidIdsInMappedContactColumn, contactColumnIdsOf, NO_VALID_WHATSAPP_ID_MESSAGE } from './phone-column';
import { useImportExcelToBoardMutation } from '@common-reducers/backend-api/backend-api';

type Props = {
    selectedBoardId: any;
    importJsonData: any[];
    handleFinishImporting?: (boardId?: string) => void;
    goNext: (isFromSheets?: boolean) => void;
}

export default function ImportNavButtons({
    selectedBoardId,
    importJsonData,
    handleFinishImporting,
    goNext,
}: Props) {
    const { t } = useTranslation(namespace);

    const dispatch = useAppDispatch()

    const step = useAppSelector((state) => state.ImportItemsReducer.step);
    const openedby = useAppSelector((state) => state.ModalReducer.openedby)
    const importType = useAppSelector((state) => state.ModalReducer.importType)
    const importFileName = useAppSelector((state) => state.ImportItemsReducer.importFileName);
    const selectedColumn = useAppSelector((state) => state.ImportItemsReducer.selectedColumn);
    const importDataMapping = useAppSelector((state) => state.ImportItemsReducer.importDataMapping);
    const importedContactsArray = useAppSelector(state => state.WhatsAppReducer.importedContactsArray)
    const columnsList = useAppSelector((state) => dynamicColumnList(state, selectedBoardId))

    const [importExcelToBoard] = useImportExcelToBoardMutation();

    const importError = useAppSelector((state) => state.ImportItemsReducer.importError);



    const nextButtonString = () => {
        if ((openedby === 'campaign' && step === 2) || importType === 'work') {
            return t('importExcelToBlueticks.import')
        }
        else {
            return t('importExcelToBlueticks.next')
        }
    }




    const isMappingRequired = useCallback(() => {
        const isFirstNameColumnExist = (columnsList ?? []).filter(column => !column.deleted && column.type === 'text-cell'
            && column?.title?.toLowerCase().includes('first'))?.length > 0
        const isLastNameColumnExist = (columnsList ?? []).filter(column => !column.deleted && column.type === 'text-cell'
            && column?.title?.toLowerCase().includes('last'))?.length > 0

        return (!isFirstNameColumnExist || !isLastNameColumnExist) && importType === 'whatsapp' && selectedBoardId
    }, [columnsList, importType, selectedBoardId])



    const startImport = async () => {
        dispatch(startImportToBoardThunk({ selectedBoardId, handleFinishImporting, importExcelToBoard }))
    };


    const isCSVOrSheetsNotReadyToImport = useMemo(() => {
        const mappedColumnIdsArray = Object.values(importDataMapping).filter(value => value && value !== '')
        const isSelectedColumnExists = !!selectedColumn && selectedColumn !== ''
        return (!(['whatsapp', 'whatsappWithMapping'].includes(importType)) && (!isSelectedColumnExists && mappedColumnIdsArray.length === 0))
    }, [importDataMapping, importType, selectedColumn])

    // #54: block a campaign import in which no recipient has a valid WhatsApp ID.
    const hasNoValidWhatsappId = openedby === 'campaign' && step === 2 && !(['whatsapp', 'whatsappWithMapping'].includes(importType))
        && countValidIdsInMappedContactColumn(importJsonData, importDataMapping, contactColumnIdsOf(columnsList, !selectedBoardId)) === 0

    const isWhatsappNotReadyToImport = useMemo(() => {
        return (['whatsapp', 'whatsappWithMapping'].includes(importType)) && (!importedContactsArray || importedContactsArray.length === 0)
    }, [importType, importedContactsArray])

    const isShowNextButton = useMemo(() => {
        if (step !== 2) {
            if (importType === 'whatsapp' && isMappingRequired()) {
                return true
            }
            if (!(['whatsapp', 'whatsappWithMapping'].includes(importType))) { // csv or google sheets
                if (importType && importType !== '') { // only after selecting import method
                    if (importFileName && importFileName !== "") { // only after selecting a file
                        return true
                    }
                }
            }
        }
        return false
    }, [importFileName, importType, isMappingRequired, step])

    const showBack = (step !== 2 && importType && importType !== '') || step === 2;

    return (
      <div className="flex w-full flex-col gap-2">
        {/* #89: a failed import (e.g. "No contacts found in sheet X") surfaces here instead of
            silently advancing to the success screen. */}
        {hasNoValidWhatsappId && !importError && (
            <div className="text-sm text-red-500" role="alert">
                {NO_VALID_WHATSAPP_ID_MESSAGE}
            </div>
        )}
        {importError && (
            <div className="text-sm text-red-500" role="alert">
                {importError}
            </div>
        )}
        {/* Wizard flow: Back on the far left, primary (Next/Import) on the far right.
            Mirrors directional intent and matches Material Stepper / Stripe Checkout conventions. */}
        <div className="flex w-full items-center justify-between">
            <div>
                {showBack && (
                    <Button
                        variant="outline"
                        onClick={() => {
                            if (importType === 'whatsapp') {
                                dispatch(modalActions.setImportType(''))
                            }
                            if (importType === 'whatsappWithMapping') {
                                dispatch(modalActions.setImportType('whatsapp'))
                            }
                            if (step > 0) {
                                if (step === 2) {
                                    if (openedby !== 'campaign') {
                                        dispatch(importItemsActions.setSelectedColumn(''))
                                    }
                                    dispatch(importItemsActions.setStep(step - 2))
                                }
                                else {
                                    dispatch(importItemsActions.setStep(step - 1))
                                }

                            }
                            if (step === 0 && importType) {
                                dispatch(modalActions.setImportType(''));
                                dispatch(importItemsActions.resetImport())
                            }
                            if (step === 0 && !importType) {
                                dispatch(importItemsActions.setStep(0))
                                dispatch(modalActions.setComponentToRender(''))
                                dispatch(setIsImportModalOpen(false))
                            }
                        }}
                        className={cn(
                            "text-base font-medium px-5 py-3 rounded-[5px]",
                            "border-0 transition-[0.2s]",
                            "bg-[var(--tasks-input-border)] hover:bg-[#ddd4a]",
                            "hover:text-[#a7c3e7] dark:hover:text-[#a7c3e7]",
                            "text-[var(--color-text)] dark:text-[var(--color-text)]"
                        )}
                    >
                        {t('importExcelToBlueticks.back')}
                    </Button>
                )}
            </div>

            <div className="flex items-center gap-2">
                {isShowNextButton && (
                    <Button
                        disabled={(step === 0 && importFileName === "" && importType !== 'whatsapp') || (step === 2 && selectedColumn === "")}
                        onClick={() => goNext()}
                        className={cn(
                            "text-base font-medium px-5 py-3 rounded-[5px]",
                            "border-0 text-[var(--color-surface)] dark:text-[var(--color-surface)]",
                            "bg-[var(--color-accent)] hover:bg-[var(--color-accent)]/90",
                            "dark:bg-[var(--color-accent)] dark:hover:bg-[var(--color-accent)]/90",
                            "disabled:text-[#a3b4cb] disabled:bg-[var(--tasks-input-border)]ddd4a disabled:cursor-not-allowed",
                            "dark:disabled:text-[#a3b4cb] dark:disabled:bg-[var(--tasks-input-border)]ddd4a"
                        )}
                    >
                        {nextButtonString()}
                    </Button>
                )}
                {(step === 2 || (importType === 'whatsapp' && !isMappingRequired()) || importType === 'whatsappWithMapping') &&
                    <ImportButton
                        startImport={() => startImport()}
                        isDisabled={(isWhatsappNotReadyToImport || isCSVOrSheetsNotReadyToImport || hasNoValidWhatsappId)} />
                }
            </div>
        </div>
      </div>
    )
}